import { ArrowBack as CancelIcon, Save as SaveIcon } from '@mui/icons-material';
import { LoadingButton, LocalizationProvider } from '@mui/lab';
import AdapterLuxon from '@mui/lab/AdapterLuxon/index.js';
import StaticDatePicker from '@mui/lab/StaticDatePicker/index.js';
import { Box, Collapse, InputAdornment, Stack, TextField } from '@mui/material';
import { DateTime } from 'luxon';
import type { FC } from 'react';
import { FormikNumberField } from '../../components/formik-fields/number-field.js';
import { FormikSwitch } from '../../components/formik-fields/switch.js';
import { FormikTextField } from '../../components/formik-fields/text-field.js';
import { CenterCard } from '../../components/page-layout/index.js';
import type { EditViewProps } from '../../utils/types.js';
import { CallSignField } from './call-sign-field.js';
import { PatrolTimePicker } from './time-picker.js';
import type { CallSignOptions } from './use-call-signs.js';

export interface CreatePatrolData {
  callSign: string;
  hours: number;
  name: string;
  patrol: boolean;
  when: Date;
}

type CreateViewProps = EditViewProps<CreatePatrolData> & CallSignOptions;

export const CreateView: FC<CreateViewProps> = ({
  callSigns,
  cancel,
  formik,
  restrictCallSigns,
}) => {
  return (
    <form onReset={formik.handleReset} onSubmit={formik.handleSubmit}>
      <CenterCard>
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <Stack
            direction={{ md: 'row', xs: 'column' }}
            sx={{ flexGrow: 1, justifyContent: 'space-evenly', pb: 3 }}
          >
            <Box className="date-wrapper">
              <StaticDatePicker
                disabled={formik.isSubmitting}
                minDate={DateTime.now().startOf('day')}
                onChange={(newValue: DateTime | null) => {
                  if (!newValue) return;
                  formik.values.when = DateTime.fromJSDate(formik.values.when)
                    .set({
                      day: newValue.day,
                      month: newValue.month,
                      year: newValue.year,
                    })
                    .toJSDate();
                }}
                renderInput={(params: object) => <TextField {...params} />}
                toolbarTitle="Select patrol date"
                value={DateTime.fromJSDate(formik.values.when)}
                views={['month', 'day']}
              />
            </Box>
            <Stack
              direction="column"
              spacing={3}
              sx={{ justifyContent: 'center' }}
            >
              <PatrolTimePicker
                disabled={formik.isSubmitting}
                onChange={(newValue: DateTime | null) => {
                  if (!newValue) return;
                  formik.values.when = DateTime.fromJSDate(formik.values.when)
                    .set({
                      hour: newValue.hour,
                      minute: newValue.minute,
                    })
                    .toJSDate();
                }}
                value={DateTime.fromJSDate(formik.values.when)}
              />
              <Stack
                direction="column"
                spacing={3}
                sx={{ justifyContent: 'center', px: 3, width: 320 }}
              >
                <FormikSwitch
                  formik={formik}
                  helperText="Uncheck if this is not actually a patrol (e.g. meeting or other event that doesn't require police notification)"
                  id="patrol"
                  label="Patrol"
                />
                <Collapse in={formik.values.patrol}>
                  <CallSignField
                    callSigns={callSigns}
                    formik={formik}
                    restrictCallSigns={restrictCallSigns}
                  />
                </Collapse>
                <FormikNumberField
                  formik={formik}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">hours</InputAdornment>
                    ),
                  }}
                  id="hours"
                  inputProps={{ step: 'any' }}
                  label={
                    formik.values.patrol ? 'Patrol duration' : 'Event duration'
                  }
                  type="number"
                />
                <FormikTextField
                  formik={formik}
                  id="name"
                  label={formik.values.patrol ? 'Patrol name' : 'Event name'}
                />
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ justifyContent: 'end' }}
                >
                  <LoadingButton
                    color="primary"
                    loading={formik.isSubmitting}
                    onClick={cancel}
                    startIcon={<CancelIcon />}
                  >
                    Cancel
                  </LoadingButton>
                  <LoadingButton
                    color="primary"
                    loading={formik.isSubmitting}
                    startIcon={<SaveIcon />}
                    type="submit"
                    variant="contained"
                  >
                    Create
                  </LoadingButton>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </LocalizationProvider>
      </CenterCard>
    </form>
  );
};
